import { Matrix4 } from "three";

interface TreatmentPlanData {
  maxStepNumber: number;
  centeringOffset: Matrix4;
  upperSteps: number;
  lowerSteps: number;
  upperOverCorrectionSteps?: number;
  lowerOverCorrectionSteps?: number;
  OBOJ: OverbiteOverjetData;
  case_id?: string;
  case_uuid?: string;
  boltonAnalysisInfo?: BoltonAnalysisDataNumeric;
  lightLayer: number[];
  cameraLayer: number[];
  gumLayer: number;
  isActive: Record<navBarFeatures, boolean>;
  has: Record<hasFeatures, boolean>;
  is: Record<isFeatures, boolean>;
  toggles: Record<otherFeatures, boolean>;
  typed: Record<typedFeatures, string>;
}
type typedFeatures = "selectedMovement" | "toggledRoot";
type hasFeatures =
  | "hasAttachments"
  | "hasElastics"
  | "hasPontics"
  | "hasBiteJump"
  | "hasRoots"
  | "hasIPR"
  | "hasPhotosXrays";

type isFeatures = "isPassiveAligner" | "isStartTogether" | "isCBCT" | "isRoot";

type navBarFeatures =
  | "attachment"
  | "ipr"
  | "elastics"
  | "roots"
  | "pontics"
  | "superImpose"
  | "occ"
  | "grid"
  | "split"
  | "numbering"
  | "biteJump"
  | "showRecords"
  | "showRuler"
  | "showOverbite"
  | "showBolton"
  | "showStaging"
  | "showMovement"
  | "showUpper"
  | "showLower";

type otherFeatures =
  | "approveDemo"
  | "showReject"
  | "showApprove"
  | "startRevision"
  | "show3DControls"
  | "showContModification"
  | "showBackGroundColorSlider"
  | "compareModifications"
  | "expand";

// Bolton Analysis Types FDI
type ToothNumber =
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 31
  | 32
  | 33
  | 34
  | 35
  | 36
  | 37
  | 38
  | 41
  | 42
  | 43
  | 44
  | 45
  | 46
  | 47
  | 48;

type ToothWidthTableNumeric = Record<ToothNumber, number | null>;

export type BoltonAnalysisDataNumeric = {
  anteriorRatio: number | null;
  overAllRatio: number | null;
  upperArchLength: number | null;
  lowerArchLength: number | null;
  toothWidthTable: ToothWidthTableNumeric;
};

// Overbite/Overjet Types
export type OverbiteOverjetData = {
  overbite: {
    11?: { initial: number; final: number };
    21?: { initial: number; final: number };
    31?: { initial: number; final: number };
    41?: { initial: number; final: number };
  };
  overjet: {
    11?: { initial: number; final: number };
    21?: { initial: number; final: number };
    31?: { initial: number; final: number };
    41?: { initial: number; final: number };
  };
};

export const globalData: TreatmentPlanData = {
  maxStepNumber: 0,
  centeringOffset: new Matrix4(),
  upperSteps: 0,
  lowerSteps: 0,
  upperOverCorrectionSteps: 0,
  lowerOverCorrectionSteps: 0,
  OBOJ: {
    overbite: {
      11: { initial: 0, final: 0 },
      21: { initial: 0, final: 0 },
      31: { initial: 0, final: 0 },
      41: { initial: 0, final: 0 },
    },
    overjet: {
      11: { initial: 0, final: 0 },
      21: { initial: 0, final: 0 },
      31: { initial: 0, final: 0 },
      41: { initial: 0, final: 0 },
    },
  },
  case_id: undefined,
  case_uuid: undefined,
  lightLayer: [0, 1],
  cameraLayer: [0, 1],
  gumLayer: 0,
  boltonAnalysisInfo: {} as BoltonAnalysisDataNumeric,
  isActive: {
    showLower: true,
    showUpper: true,
    attachment: true,
    ipr: true,
    elastics: true,
    roots: false,
    pontics: true,
    superImpose: false,
    occ: false,
    grid: false,
    split: false,
    numbering: false,
    showRuler: false,
    showOverbite: false,
    showBolton: false,
    showStaging: false,
    showMovement: false,
    showRecords: false,
    biteJump: true,
  },
  has: {
    hasAttachments: false,
    hasElastics: false,
    hasIPR: false,
    hasPontics: false,
    hasBiteJump: false,
    hasRoots: false,
    hasPhotosXrays: false,
  },
  is: {
    isPassiveAligner: false,
    isStartTogether: false,
    isCBCT: false,
    isRoot: false,
  },
  toggles: {
    approveDemo: false,
    showReject: false,
    showApprove: false,
    startRevision: false,
    show3DControls: false,
    showContModification: false,
    expand: false,
    showBackGroundColorSlider: false,
    compareModifications: false,
  },
  typed: {
    toggledRoot: "GUM",
    selectedMovement: "Extrusion/Intrusion",
  },
};

let setToggleRef: (name: string, value?: boolean) => void;
export let isGlobalStoreToggleDefined = false;

export function setGlobalStoreToggleRef(
  func: (name: string, value?: boolean) => void,
): void {
  setToggleRef = func;
  isGlobalStoreToggleDefined = true;
}
export function setToggle(name: string, value?: boolean) {
  let targetObject: Record<string, boolean> | Record<string, string> | null =
    null;
  // console.log(`key = ${name}`);

  // Check for the key's existence in each of the three potential objects
  if (name in globalData.has) {
    targetObject = globalData.has;
  } else if (name in globalData.is) {
    targetObject = globalData.is;
  } else if (name in globalData.isActive) {
    targetObject = globalData.isActive;
  } else if (name in globalData.toggles) {
    targetObject = globalData.toggles;
  } else if (name in globalData.typed) {
    targetObject = globalData.typed;
  }
  if (targetObject === null) {
    console.error(
      `Key "${name}" not found in any of the known toggle objects.`,
    );
  } else {
    const currentValue = targetObject[name];

    targetObject[name] = value ?? !currentValue;
  }

  setToggleRef(name, value);
}
